/**
 * input/simulator.ts — Desktop fallback input (mouse-look + WASD)
 * M1: Mouse look, WASD movement, scroll speed, shift boost
 *      Implements the same InputFrame abstraction for future reuse
 */

import * as THREE from 'three';

// Mouse look state
let _yaw = 0;
let _pitch = 0;
const MOUSE_SENSITIVITY = 0.002;
const PITCH_LIMIT = Math.PI / 3;

// Movement state
const keys: Record<string, boolean> = {};
let _speed = 5.0;
const BASE_SPEED = 5.0;
const BOOST_SPEED = 20.0;

// Pointer lock
let _locked = false;

function onMouseMove(e: MouseEvent) {
  if (!_locked) return;
  _yaw -= e.movementX * MOUSE_SENSITIVITY;
  _pitch -= e.movementY * MOUSE_SENSITIVITY;
  _pitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, _pitch));
}

function onKeyDown(e: KeyboardEvent) {
  keys[e.key.toLowerCase()] = true;
  if (e.key.toLowerCase() === 'shift') {
    _speed = BOOST_SPEED;
  }
}

function onKeyUp(e: KeyboardEvent) {
  keys[e.key.toLowerCase()] = false;
  if (e.key.toLowerCase() === 'shift') {
    _speed = BASE_SPEED;
  }
}

function onWheel(e: WheelEvent) {
  const delta = (e.deltaY > 0 ? -1 : 1);
  _speed = Math.max(1, Math.min(50, _speed + delta * 2));
}

function onPointerLockChange() {
  _locked = document.pointerLockElement !== null;
  if (!_locked) {
    document.body.style.cursor = 'default';
  }
}

export function createSimulator(camera: THREE.Camera, _scene: THREE.Scene): void {
  // Set initial camera position for desktop mode
  camera.position.set(0, 1.6, 0);

  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('keydown', onKeyDown);
  document.addEventListener('keyup', onKeyUp);
  document.addEventListener('wheel', onWheel, { passive: true });
  document.addEventListener('pointerlockchange', onPointerLockChange);

  // Click to lock pointer
  document.body.addEventListener('click', () => {
    if (!_locked) {
      document.body.requestPointerLock();
    }
  });

  document.body.style.cursor = 'pointer';
  console.log('[SIM] Desktop simulator active — click to lock mouse, WASD to move');
}

const _moveVec = new THREE.Vector3();
const _euler = new THREE.Euler(0, 0, 0, 'YXZ');
const _forward = new THREE.Vector3();
const _right = new THREE.Vector3();

export function updateSimulator(dtMs: number, camera: THREE.Camera): void {
  if (!_locked) return;

  const dt = Math.min(dtMs / 1000, 0.1); // clamp dt

  // Apply mouse rotation
  _euler.set(_pitch, _yaw, 0);
  camera.quaternion.setFromEuler(_euler);

  // Movement
  _moveVec.set(0, 0, 0);
  camera.getWorldDirection(_forward);
  _right.crossVectors(camera.up, _forward).normalize();

  if (keys['w']) _moveVec.add(_forward);
  if (keys['s']) _moveVec.sub(_forward);
  if (keys['a']) _moveVec.add(_right);
  if (keys['d']) _moveVec.sub(_right);
  if (keys['q']) _moveVec.y -= 1;
  if (keys['e']) _moveVec.y += 1;

  if (_moveVec.lengthSq() > 1) _moveVec.normalize();

  camera.position.addScaledVector(_moveVec, _speed * dt);
}

export function getSimulatorState() {
  return {
    speed: _speed,
    locked: _locked,
    yaw: _yaw,
    pitch: _pitch,
  };
}
